import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import {
  type GroupType,
  type UpdateCostOffsetCodeMutation,
  type UpdateCostOffsetCodeMutationVariables,
} from 'graphql-query-contracts';
import { updateCostOffsetCodeMutation } from 'graphql-queries';
import type { CostOffsetCode } from 'domains-billing-types';
import { UpdateCostOffsetCodeError } from './errors/UpdateCostOffsetCodeError';

export interface UpdateCostOffsetCodeInput {
  id: string;
  code: string;
  description?: string;
  groupId: string;
  groupType: GroupType;
}

export interface UseUpdateCostOffsetCodeResult {
  updateCostOffsetCode: (
    input: UpdateCostOffsetCodeInput
  ) => Promise<CostOffsetCode>;
  loading: boolean;
  error: Error | undefined;
}

export function useUpdateCostOffsetCode(): UseUpdateCostOffsetCodeResult {
  const [mutate, { loading, error }] = useMutation<
    UpdateCostOffsetCodeMutation,
    UpdateCostOffsetCodeMutationVariables
  >(updateCostOffsetCodeMutation);

  const updateCostOffsetCode = useCallback(
    async (input: UpdateCostOffsetCodeInput): Promise<CostOffsetCode> => {
      try {
        const result = await mutate({
          variables: {
            updateCostOffsetCodeRequest: {
              id: input.id,
              code: input.code,
              description: input.description,
              groupId: input.groupId,
              groupType: input.groupType,
            },
          },
        });

        const updated = result.data?.updateCostOffsetCode?.costOffsetCode;

        if (!updated) {
          throw new UpdateCostOffsetCodeError();
        }

        return {
          id: updated.id,
          code: updated.code,
          description: updated.description ?? undefined,
          groupId: updated.groupId,
          groupType: updated.groupType,
        };
      } catch (err) {
        if (err instanceof UpdateCostOffsetCodeError) {
          throw err;
        }

        throw new UpdateCostOffsetCodeError(
          err instanceof Error ? err.message : undefined,
          { cause: err }
        );
      }
    },
    [mutate]
  );

  return {
    updateCostOffsetCode,
    loading,
    error: error ?? undefined,
  };
}
